import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Anchor, Skeleton, Switch, Tabs } from '@mantine/core';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'tabler-icons-react';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';
import AppTitle from '../../common/AppTitle';
import HeaderView from '../../common/HeaderView';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import TabsContent from '../../common/TabsContent';
import PaymentPlanList from './PaymentPlanList';
import CustomerInvoiceList from './CustomerInvoiceList';
import CustomerInvoicePaymentsModal from './CustomerInvoicePaymentsModal';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { getResponsiveStyle as rs } from '../../../helpers/styles';
import {
  getCustomerPaymentAlerts,
  PROCESSING_COLOR
} from '../../../helpers/customerPaymentsHelper';

const TABS_ENUM = {
  PLANS: 'PLANS',
  INVOICES: 'INVOICES'
};

const CustomerPaymentsList = () => {
  const mqIndex = useMediaQueryIndex();
  const hasFetched = useRef(false);
  const [activeTab, setActiveTab] = useState(TABS_ENUM.PLANS);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    invoice: null
  });
  const { state, fetchCustomerPayments } = useContext(RegistrationContext);

  const isLoading =
    !fetchError && (!hasFetched.current || state.customerPayments.loading);
  const customerPayments = state.customerPayments.value;
  const paymentPlans = customerPayments?.paymentPlans ?? [];
  const invoices = customerPayments?.invoices ?? [];
  const {
    hasFailedPaymentPlan,
    hasProcessingPaymentPlan,
    hasOutstandingInvoice
  } = getCustomerPaymentAlerts(customerPayments);
  const hasPaymentAlert =
    hasFailedPaymentPlan || hasProcessingPaymentPlan || hasOutstandingInvoice;

  const tabs = [
    {
      label: isLoading
        ? 'Payment plans'
        : `Payment plans (${paymentPlans.length})`,
      value: TABS_ENUM.PLANS
    },
    {
      label: isLoading ? 'Invoices' : `Invoices (${invoices.length})`,
      value: TABS_ENUM.INVOICES
    }
  ];

  useEffect(() => {
    setFetchError(null);
    fetchCustomerPayments(includeCompleted, null, (error, type, data, status) =>
      setFetchError({ message: [404, 409].includes(status) ? error : null })
    );
    hasFetched.current = true;
  }, [includeCompleted]);

  return (
    <HeaderView
      maxViewWidth={1100}
      viewProps={{
        style: {
          gap: rs([10, 10, 20], mqIndex),
          marginBottom: rs([10, 10, 40], mqIndex)
        }
      }}
    >
      <CustomerInvoicePaymentsModal
        invoice={modalState.invoice}
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
      />

      <AppFlexbox
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10
        }}
      >
        {isLoading ? (
          <Skeleton
            height={44}
            style={{ marginTop: rs([10, 10, 0], mqIndex) }}
            width={250}
          />
        ) : (
          <AppTitle style={{ marginTop: rs([5, 5, 0], mqIndex) }}>
            Your Payments
          </AppTitle>
        )}

        <Switch
          checked={includeCompleted}
          label="Show completed"
          onChange={(e) => setIncludeCompleted(e.currentTarget.checked)}
        />
      </AppFlexbox>

      {!isLoading && !fetchError && hasPaymentAlert && (
        <Alert
          color={hasFailedPaymentPlan ? 'red' : PROCESSING_COLOR}
          icon={
            hasFailedPaymentPlan ? (
              <AlertTriangle style={{ width: 24, height: 24 }} />
            ) : (
              <Clock style={{ width: 24, height: 24 }} />
            )
          }
          p="xs"
          variant="light"
        >
          <AppStack style={{ gap: 2 }}>
            {hasFailedPaymentPlan && (
              <AppText style={{ fontSize: 14 }} weight={500}>
                A payment failed and new card details are needed.
              </AppText>
            )}
            {hasProcessingPaymentPlan && (
              <AppText style={{ fontSize: 14 }} weight={500}>
                A scheduled payment is still processing. No action is needed
                right now.
              </AppText>
            )}
            {hasOutstandingInvoice && (
              <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                <AppText style={{ fontSize: 14 }} weight={500}>
                  You have an outstanding invoice.
                </AppText>
                <Anchor
                  component="button"
                  onClick={() => setActiveTab(TABS_ENUM.INVOICES)}
                  style={{ fontSize: 14 }}
                  type="button"
                >
                  View invoices
                </Anchor>
              </AppFlexbox>
            )}
          </AppStack>
        </Alert>
      )}

      {fetchError ? (
        <AppCard>
          <AppFlexbox
            style={{
              alignSelf: 'center',
              justifyContent: 'center',
              padding: 40,
              gap: 10
            }}
          >
            <AppText style={{ fontSize: 16 }}>
              {fetchError.message ??
                'We couldn’t load your payments right now. Please try again shortly.'}
            </AppText>
          </AppFlexbox>
        </AppCard>
      ) : !isLoading && paymentPlans.length === 0 && invoices.length === 0 ? (
        <AppCard>
          <AppFlexbox
            style={{
              alignSelf: 'center',
              justifyContent: 'center',
              padding: 40,
              gap: 10
            }}
          >
            <AppText style={{ fontSize: 16 }}>
              You have no scheduled payments or invoices.
            </AppText>
            <Anchor component={Link} to="/orders">
              <AppText style={{ fontSize: 16 }}>View your orders</AppText>
            </Anchor>
          </AppFlexbox>
        </AppCard>
      ) : (
        <AppCard style={{ padding: rs([10, 10, 16], mqIndex) }}>
          <TabsContent
            activeTab={activeTab}
            color="blue"
            isLoading={isLoading}
            onTabChange={setActiveTab}
            tabs={tabs}
            variant="outline"
          >
            <Tabs.Panel pt={16} value={TABS_ENUM.PLANS}>
              <PaymentPlanList
                isLoading={isLoading}
                paymentPlans={paymentPlans}
              />
            </Tabs.Panel>

            <Tabs.Panel pt={16} value={TABS_ENUM.INVOICES}>
              <CustomerInvoiceList
                invoices={invoices}
                isLoading={isLoading}
                onViewPayments={(invoice) =>
                  setModalState({ isOpen: true, invoice })
                }
              />
            </Tabs.Panel>
          </TabsContent>
        </AppCard>
      )}
    </HeaderView>
  );
};

export default CustomerPaymentsList;
